import { useEffect, useLayoutEffect, useRef } from "react";

const SCROLL_THRESHOLD = 200;
const THROTTLE_MS = 300;

interface UseMessageListScrollParams {
  messages: Array<{ id?: string; sender_id: string }>;
  isLoading: boolean;
  currentUserId: string;
  chatId?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

interface UseMessageListScrollReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  messagesStartRef: React.RefObject<HTMLDivElement>;
}

export const useMessageListScroll = ({
  messages,
  isLoading,
  currentUserId,
  chatId,
  onLoadMore,
  hasMore = false
}: UseMessageListScrollParams): UseMessageListScrollReturn => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesStartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToBottomRef = useRef(false);
  const previousScrollHeightRef = useRef(0);
  const previousScrollTopRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  const lastLoadMoreTimeRef = useRef(0);
  const chatKey = chatId ?? currentUserId;
  const previousMessagesLengthRef = useRef(0);
  const previousLastMessageIdRef = useRef<string | null>(null);
  const isInitialPhaseRef = useRef(true);

  useEffect(() => {
    hasScrolledToBottomRef.current = false;
    previousScrollHeightRef.current = 0;
    previousScrollTopRef.current = 0;
    isLoadingMoreRef.current = false;
    lastLoadMoreTimeRef.current = 0;
    previousMessagesLengthRef.current = 0;
    previousLastMessageIdRef.current = null;
    isInitialPhaseRef.current = true;
  }, [chatKey]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || isLoading || messages.length === 0) return;
    if (!isInitialPhaseRef.current) return;
    if (isLoadingMoreRef.current) return;

    container.scrollTop = container.scrollHeight;

    const scrollToBottomIfNeeded = () => {
      if (
        isInitialPhaseRef.current &&
        !isLoadingMoreRef.current &&
        container.scrollTop + container.clientHeight <
          container.scrollHeight - 1
      ) {
        container.scrollTop = container.scrollHeight;
      }
    };

    const observer = new ResizeObserver(scrollToBottomIfNeeded);

    Array.from(container.children).forEach((child) => observer.observe(child));

    const timeout = setTimeout(() => {
      isInitialPhaseRef.current = false;
      observer.disconnect();
    }, 1000);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [chatKey, isLoading]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || messages.length === 0) {
      return;
    }

    const messagesLength = messages.length;
    const lastMessage = messages[messagesLength - 1];
    // Use actual message ID to detect new messages, not count-based ID
    const lastMessageId =
      lastMessage.id || `${lastMessage.sender_id}-${messagesLength}`;

    const scrollToBottom = () => {
      if (container) container.scrollTop = container.scrollHeight;
    };

    // Case 1: Initial scroll bottom on first load
    if (!hasScrolledToBottomRef.current && !isLoading) {
      hasScrolledToBottomRef.current = true;
      scrollToBottom();
      previousMessagesLengthRef.current = messagesLength;
      previousLastMessageIdRef.current = lastMessageId;
      return;
    }

    // Case 2: Scroll to bottom when user sends a new message
    const isNewMessage = messagesLength > previousMessagesLengthRef.current;
    const isNewMessageFromCurrentUser =
      isNewMessage &&
      lastMessage.sender_id === currentUserId &&
      lastMessageId !== previousLastMessageIdRef.current;

    if (isNewMessageFromCurrentUser && !isLoading) {
      scrollToBottom();
      requestAnimationFrame(scrollToBottom);
      previousMessagesLengthRef.current = messagesLength;
      previousLastMessageIdRef.current = lastMessageId;
      return;
    }

    // Case 3: Restore scroll position when loading older messages
    if (isLoadingMoreRef.current && isNewMessage) {
      const currentScrollHeight = container.scrollHeight;
      const heightDifference =
        currentScrollHeight - previousScrollHeightRef.current;

      if (heightDifference > 0) {
        container.scrollTop = previousScrollTopRef.current + heightDifference;
        isLoadingMoreRef.current = false;
      }
    }

    // Update refs for next comparison
    if (isNewMessage) {
      previousMessagesLengthRef.current = messagesLength;
      previousLastMessageIdRef.current = lastMessageId;
    }
  }, [messages.length, currentUserId, isLoading, chatKey]);

  useEffect(() => {
    const startElement = messagesStartRef.current;
    const container = containerRef.current;

    if (!startElement || !container || !onLoadMore || !hasMore || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const now = Date.now();
        const timeSinceLastCall = now - lastLoadMoreTimeRef.current;

        if (
          entry.isIntersecting &&
          !isLoadingMoreRef.current &&
          hasMore &&
          !isLoading &&
          timeSinceLastCall >= THROTTLE_MS
        ) {
          previousScrollTopRef.current = container.scrollTop;
          previousScrollHeightRef.current = container.scrollHeight;
          isLoadingMoreRef.current = true;
          lastLoadMoreTimeRef.current = now;
          onLoadMore();
        }
      },
      {
        root: container,
        rootMargin: `${SCROLL_THRESHOLD}px 0px 0px 0px`,
        threshold: 0.1
      }
    );

    observer.observe(startElement);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoading]);

  useEffect(() => {
    if (isLoadingMoreRef.current && !isLoading) {
      const timeout = setTimeout(() => {
        if (isLoadingMoreRef.current) {
          isLoadingMoreRef.current = false;
        }
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, messages.length]);

  return {
    containerRef,
    messagesEndRef,
    messagesStartRef
  };
};
